import { useEffect, useRef } from 'react';
import { COLOR_LABELS } from '../editor';

interface Props {
  regions: string[][];
  /** Called for each cell as the pointer enters it during a drag. */
  onCellEnter: (cell: { row: number; col: number }, isFirst: boolean) => void;
  /** Called when a drag ends normally (mouseup / touchend). */
  onDragEnd?: () => void;
  /** Called when a touch long-press aborts the drag for color picking. */
  onDragAbort?: () => void;
  /** Right-click / long-press a cell to sample its color. */
  onPickColor?: (color: string) => void;
  /** Cells outlined as the current editable selection (restriction). */
  selected?: Set<string>;
  /** Cells to mark with a queen (e.g. a target-solution preview). */
  queens?: Set<string>;
  /** Cells to mark with an ✕ (visual scratch marks, not part of the puzzle). */
  marks?: Set<string>;
  /** Drop the paint cursor — the parent is ignoring drags (e.g. the generator tab). */
  readOnly?: boolean;
}

export function EditorBoard({ regions, onCellEnter, onDragEnd, onDragAbort, onPickColor, selected, queens, marks, readOnly }: Props) {
  const size = regions.length;
  const boardRef = useRef<HTMLDivElement>(null);
  const drag = useRef({ active: false, keys: new Set<string>() });

  const onCellEnterRef = useRef(onCellEnter);
  onCellEnterRef.current = onCellEnter;
  const onDragEndRef = useRef(onDragEnd);
  onDragEndRef.current = onDragEnd;
  const onDragAbortRef = useRef(onDragAbort);
  onDragAbortRef.current = onDragAbort;
  const onPickColorRef = useRef(onPickColor);
  onPickColorRef.current = onPickColor;
  const regionsRef = useRef(regions);
  regionsRef.current = regions;

  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const el = boardRef.current!;

    function cellFromPoint(x: number, y: number) {
      const target = document.elementFromPoint(x, y);
      const cell = (target as HTMLElement | null)?.closest<HTMLElement>('.cell');
      if (!cell) return null;
      const row = Number(cell.dataset.row);
      const col = Number(cell.dataset.col);
      if (isNaN(row) || isNaN(col)) return null;
      return { row, col };
    }

    function add(cell: { row: number; col: number }, isFirst = false) {
      const key = `${cell.row},${cell.col}`;
      if (drag.current.keys.has(key)) return;
      drag.current.keys.add(key);
      onCellEnterRef.current(cell, isFirst);
    }

    function start(cell: { row: number; col: number } | null) {
      if (!cell) return;
      drag.current = { active: true, keys: new Set() };
      add(cell, true);
    }

    function end() {
      if (!drag.current.active) return;
      drag.current.active = false;
      onDragEndRef.current?.();
    }

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      start(cellFromPoint(e.clientX, e.clientY));
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!drag.current.active) return;
      const cell = cellFromPoint(e.clientX, e.clientY);
      if (cell) add(cell);
    };
    const onMouseUp = () => end();
    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      longPressFired.current = false;
      const t = e.touches[0];
      touchStartPos.current = { x: t.clientX, y: t.clientY };
      const cell = cellFromPoint(t.clientX, t.clientY);
      // Capture the cell's original color before start() paints over it.
      const originalColor = cell ? regionsRef.current[cell.row]?.[cell.col] : undefined;
      start(cell);

      if (cell && originalColor && onPickColorRef.current) {
        longPressTimer.current = setTimeout(() => {
          longPressTimer.current = null;
          longPressFired.current = true;
          drag.current = { active: false, keys: new Set() };
          onDragAbortRef.current?.();
          onPickColorRef.current!(originalColor);
          navigator.vibrate?.(50);
        }, 500);
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (!drag.current.active) return;
      if (longPressTimer.current && touchStartPos.current) {
        const dx = e.touches[0].clientX - touchStartPos.current.x;
        const dy = e.touches[0].clientY - touchStartPos.current.y;
        if (dx * dx + dy * dy > 225) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
        }
      }
      const cell = cellFromPoint(e.touches[0].clientX, e.touches[0].clientY);
      if (cell) add(cell);
    };
    const onTouchEnd = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
      if (!longPressFired.current) end();
      longPressFired.current = false;
    };

    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);

    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  const cells = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      const colorIdx = COLOR_LABELS.indexOf(regions[r][c]);
      const key = `${r},${c}`;
      const cls = [
        'cell',
        `color-${colorIdx}`,
        r === 0 || regions[r - 1][c] !== regions[r][c] ? 'border-top' : '',
        c === 0 || regions[r][c - 1] !== regions[r][c] ? 'border-left' : '',
        r === size - 1 || regions[r + 1]?.[c] !== regions[r][c] ? 'border-bottom' : '',
        c === size - 1 || regions[r][c + 1] !== regions[r][c] ? 'border-right' : '',
        selected?.has(key) ? 'cell-selected' : '',
        // Same classes the game board uses, so a previewed queen is the same black glyph a
        // player places rather than the panel's light text color.
        queens?.has(key) ? 'state-queen' : '',
        !queens?.has(key) && marks?.has(key) ? 'state-x' : '',
      ].filter(Boolean).join(' ');

      cells.push(
        <div
          key={key}
          className={cls}
          data-row={r}
          data-col={c}
          onContextMenu={e => { e.preventDefault(); onPickColor?.(regions[r][c]); }}
        >
          {queens?.has(key) ? '♛' : marks?.has(key) ? '✕' : ''}
        </div>
      );
    }
  }

  return (
    <div
      ref={boardRef}
      className={`board editor-board${readOnly ? ' editor-board-readonly' : ''}`}
      style={{ '--board-size': String(size) } as React.CSSProperties}
    >
      {cells}
    </div>
  );
}
